// 1PIF import modal: pick file → preview → confirm.

import { el, clear } from './dom.js';
import { importFrom1pif, applyImport } from '../import/onepif-importer.js';
import { openSheet } from './sheet.js';

export function openImportDialog({ vault, autoLock, onDone }) {
  autoLock?.pause?.();

  const main = el('main');
  const footer = el('div', { class: 'form-actions' });
  const body = el('div', { class: 'import-body' }, [main, footer]);

  const sheetHandle = openSheet({
    kind: 'fullscreen',
    title: 'Import from 1Password (.1pif)',
    body,
    onDismiss: () => autoLock?.resume?.(),
  });

  const close = () => sheetHandle.close();

  showPicker();

  function showPicker() {
    clear(main);
    const drop = el('div', { class: 'drop-zone' }, [
      el('div', {}, ['Drop the .1pif folder or data.1pif here']),
      el('div', { style: { fontSize: '12px', marginTop: '6px' } }, ['or choose:']),
    ]);

    const fileInput = el('input', { type: 'file', accept: '.1pif,application/json,text/plain', style: { display: 'none' }, onchange: (e) => {
      const f = e.target.files?.[0];
      if (f) readFile(f);
    } });
    const folderInput = el('input', { type: 'file', webkitdirectory: true, directory: true, multiple: true, style: { display: 'none' }, onchange: (e) => {
      pickDataFile(Array.from(e.target.files ?? []));
    } });

    const buttons = el('div', { style: { display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '12px' } }, [
      el('button', { onclick: (e) => { e.stopPropagation(); folderInput.click(); } }, ['Choose folder']),
      el('button', { onclick: (e) => { e.stopPropagation(); fileInput.click(); } }, ['Choose file']),
    ]);
    drop.append(buttons);

    drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('over'); });
    drop.addEventListener('dragleave', () => drop.classList.remove('over'));
    drop.addEventListener('drop', async (e) => {
      e.preventDefault();
      drop.classList.remove('over');
      const items = e.dataTransfer?.items;
      // Folder drop: walk the entry tree to find data.1pif inside.
      if (items && items.length && items[0].webkitGetAsEntry?.()) {
        const files = await collectDroppedFiles(items);
        pickDataFile(files);
        return;
      }
      const f = e.dataTransfer?.files?.[0];
      if (f) readFile(f);
    });
    main.append(drop, fileInput, folderInput);

    clear(footer);
    footer.append(el('button', { class: 'ghost', onclick: close }, ['Cancel']));
  }

  function pickDataFile(files) {
    // Look for data.1pif, then fall back to any .1pif file.
    const data = files.find((f) => /(^|\/)data\.1pif$/i.test(f.webkitRelativePath || f.name))
      ?? files.find((f) => /\.1pif$/i.test(f.name));
    if (!data) {
      showError('No data.1pif file found in that folder.');
      return;
    }

    // Group any sibling attachments by 1Password item uuid. Path shape:
    //   <root>/attachments/<ITEM_UUID>/<filename>
    const dataPath = data.webkitRelativePath || data.name;
    const rootPrefix = dataPath.includes('/') ? dataPath.slice(0, dataPath.lastIndexOf('/') + 1) : '';
    const attachPrefix = rootPrefix + 'attachments/';
    const attachmentsByUuid = new Map();
    for (const f of files) {
      const p = f.webkitRelativePath || f.name;
      if (!p.startsWith(attachPrefix)) continue;
      const rest = p.slice(attachPrefix.length);
      const slash = rest.indexOf('/');
      if (slash <= 0) continue;
      const uuid = rest.slice(0, slash);
      const filename = rest.slice(slash + 1);
      if (!filename || filename.endsWith('/')) continue;
      if (!attachmentsByUuid.has(uuid)) attachmentsByUuid.set(uuid, []);
      attachmentsByUuid.get(uuid).push({ file: f, filename });
    }

    readFile(data, attachmentsByUuid);
  }

  async function collectDroppedFiles(items) {
    const out = [];
    const walk = async (entry, path) => {
      if (entry.isFile) {
        await new Promise((res) => entry.file((f) => {
          // attach a webkitRelativePath-like field
          try { Object.defineProperty(f, 'webkitRelativePath', { value: path + entry.name }); } catch { /* read-only on some browsers */ }
          out.push(f);
          res();
        }));
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        const entries = await new Promise((res) => reader.readEntries(res));
        for (const e of entries) await walk(e, path + entry.name + '/');
      }
    };
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry?.();
      if (entry) await walk(entry, '');
    }
    return out;
  }

  async function readFile(file, attachmentsByUuid = null) {
    clear(main);
    main.append(el('div', {}, [`Parsing ${file.name}…`]));
    let text;
    try {
      text = await file.text();
    } catch (err) {
      showError(`Could not read file: ${err.message ?? err}`);
      return;
    }
    let result;
    try {
      result = importFrom1pif(text);
    } catch (err) {
      showError(`Parse failed: ${err.message ?? err}`);
      return;
    }
    showPreview(result, attachmentsByUuid);
  }

  function showPreview(result, attachmentsByUuid) {
    clear(main);

    const lines = Object.entries(result.summary.byType)
      .map(([type, n]) => `${n} ${type}${n === 1 ? '' : 's'}`)
      .join(', ');

    main.append(
      el('p', {}, [`Found ${result.summary.total} items. Will import ${result.entries.length}: ${lines || '(none)'}.`]),
    );

    let totalAttachmentFiles = 0;
    if (attachmentsByUuid) {
      for (const arr of attachmentsByUuid.values()) totalAttachmentFiles += arr.length;
    }

    const skipNotes = [];
    if (result.summary.trashed) skipNotes.push(`${result.summary.trashed} trashed items (excluded)`);
    if (totalAttachmentFiles > 0) {
      skipNotes.push(`${totalAttachmentFiles} attachment files will be encrypted and imported`);
    } else if (result.summary.withAttachments) {
      skipNotes.push(`${result.summary.withAttachments} items reference attachments — drop the .1pif folder (not just data.1pif) to include them`);
    }
    if (result.summary.unmapped) skipNotes.push(`${result.summary.unmapped} items of unsupported types`);

    if (skipNotes.length) {
      main.append(el('p', { style: { color: 'var(--muted)', fontSize: '13px', marginTop: '8px' } }, [skipNotes.join(' · ')]));
    }

    if (result.unmapped.length) {
      main.append(detailsList(`${result.unmapped.length} unsupported`, result.unmapped.map((u) => `${u.title || '(untitled)'} — ${u.typeName}`)));
    }
    if (result.attachmentTitles.length) {
      main.append(detailsList(`${result.attachmentTitles.length} with attachments`, result.attachmentTitles));
    }
    if (result.trashedTitles.length) {
      main.append(detailsList(`${result.trashedTitles.length} trashed (skipped)`, result.trashedTitles));
    }

    clear(footer);
    footer.append(
      el('button', { class: 'ghost', onclick: close }, ['Cancel']),
      el('button', { class: 'primary', onclick: async () => {
        footer.querySelectorAll('button').forEach((b) => (b.disabled = true));
        try {
          const byUuid = attachmentsByUuid ? await readAttachmentBytes(attachmentsByUuid) : null;
          const counts = await applyImport(vault, result, byUuid);
          close();
          onDone?.(counts);
        } catch (err) {
          showError(err.message ?? String(err));
        }
      } }, [`Import ${result.entries.length} items${totalAttachmentFiles ? ` + ${totalAttachmentFiles} files` : ''}`]),
    );
  }

  async function readAttachmentBytes(attachmentsByUuid) {
    const out = new Map();
    for (const [uuid, files] of attachmentsByUuid) {
      const decoded = [];
      for (const { file, filename } of files) {
        const buf = await file.arrayBuffer();
        decoded.push({
          name: filename,
          type: file.type || guessMimeFromName(filename),
          bytes: new Uint8Array(buf),
        });
      }
      out.set(uuid, decoded);
    }
    return out;
  }

  function guessMimeFromName(name) {
    const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
    return ({
      png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
      webp: 'image/webp', svg: 'image/svg+xml', heic: 'image/heic',
      pdf: 'application/pdf', txt: 'text/plain', json: 'application/json',
    })[ext] ?? 'application/octet-stream';
  }

  function detailsList(label, items) {
    return el('details', { style: { marginTop: '12px' } }, [
      el('summary', { style: { cursor: 'pointer', color: 'var(--muted)' } }, [label]),
      el('ul', { style: { color: 'var(--muted)', fontSize: '13px', lineHeight: '1.6', maxHeight: '200px', overflowY: 'auto' } },
        items.slice(0, 200).map((s) => el('li', {}, [s])),
      ),
    ]);
  }

  function showError(msg) {
    clear(main);
    main.append(el('div', { class: 'error', style: { color: 'var(--danger)' } }, [msg]));
    clear(footer);
    footer.append(el('button', { onclick: showPicker }, ['Try again']));
  }
}
